import { Routes } from '@angular/router';
import { AuthCallbackPage, AuthRedirectingPage, SignedOutPage, authGuard } from 'eshava-shared';

import { NotFoundPage } from '@portal/core/layout/not-found-page';
import { startRouteGuard } from '@portal/core/auth/start-route.guard';
import { ProductRequired } from '@portal/core/layout/product-required';
import { Shell } from '@portal/core/layout/shell';

/**
 * Lazy loading comes from the dynamic import and the exported route array, not from a project
 * boundary: the alias points at a folder, and esbuild produces the same chunk it would for a
 * library. That is the whole reason the areas are folders.
 *
 * **Nothing renders without a token** except the two routes at the bottom, and neither shows
 * anything of the application.
 */
export const appRoutes: Routes = [
  {
    path: '',
    component: Shell,
    /* One guard, on the root route -- not repeated per feature. A route without a token does not
       render and then redirect, it redirects. */
    canActivate: [authGuard],
    children: [
      {
        /*
         * **Derived from the user's permissions**, which is one of the two things the note here used
         * to say a start route may be. It was `customers` on the reasoning that it is the everyday
         * work -- true, and still a route a restricted user may not enter, which threw them onto a
         * refusal the moment they signed in. Now it opens wherever the sidebar's first entry points,
         * and the refusal page only when there is no entry at all.
         *
         * A **dashboard** is still the intended answer (see TODO.md), and it is the other kind:
         * reachable by everyone, so it needs no derivation.
         */
        path: '',
        pathMatch: 'full',
        canActivate: [startRouteGuard],
        /* Nothing renders: the guard always answers with a UrlTree. */
        children: []
      },
      {
        /* Where a refused route lands, and it is deliberately not guarded -- guarding the page that
           explains a missing permission would send it to itself. Inside the shell so the sidebar
           stays, and outside `ProductRequired` so it does not demand a product first. */
        path: 'no-permission',
        loadComponent: () => import('@portal/core/auth/no-permission-page').then(m => m.NoPermissionPage)
      },
      {
        path: 'settings',
        loadChildren: () => import('@portal/features/settings/settings.routes').then(m => m.settingsRoutes)
      },
      {
        /*
         * The four areas that are shown **for one product**, under one component that answers the
         * case where none is selected. Grouped here rather than checked inside each area: the areas
         * do not have to know the rule, and a fifth gets it by being routed underneath.
         *
         * A componentless parent would not do -- the answer is something to render, not a redirect.
         * The lazy chunks are unaffected: `loadChildren` still produces one per area.
         */
        path: '',
        component: ProductRequired,
        children: [
          {
            path: 'applications',
            loadChildren: () =>
              import('@portal/features/applications/applications.routes').then(m => m.applicationsRoutes)
          },
          {
            path: 'permissions',
            loadChildren: () =>
              import('@portal/features/permissions/permissions.routes').then(m => m.permissionsRoutes)
          },
          {
            path: 'licenses',
            loadChildren: () => import('@portal/features/licenses/licenses.routes').then(m => m.licensesRoutes)
          },
          {
            path: 'customers',
            loadChildren: () => import('@portal/features/customers/customers.routes').then(m => m.customersRoutes)
          }
        ]
      }
    ]
  },
  {
    /* Where Cardinal sends the user back. Renders nothing of the application -- it exchanges the code
       and navigates on. */
    path: 'auth/callback',
    component: AuthCallbackPage
  },
  {
    /* Shown for the moment between the guard cancelling a navigation and the browser following the
       redirect. Without it that moment is a blank page. */
    path: 'auth/redirecting',
    component: AuthRedirectingPage
  },
  {
    /* The third anonymous route. Without it, signing out lands on the login screen and the sign-out
       looks like it did nothing -- there is nowhere else to be when everything needs a token. */
    path: 'auth/signed-out',
    component: SignedOutPage
  },
  {
    /* Outside the shell on purpose: an unknown address must not show navigation or context. */
    path: '**',
    component: NotFoundPage
  }
];